import { Button, Message } from '@timelyapp/react';
import { trim } from 'lodash-es';
import React, { useRef } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { addAppMiddleware } from 'redux/middleware/dynamicMiddleware';
import type { actions as actionsType } from 'redux/modules/synthetic';
import { getHasEnabledTic } from 'redux/selectors/user';
import type { ThunkDispatch } from 'types/redux';
import type { StoreWithReducerInjector } from 'utils/redux';
import type { MemorySpecBase } from 'utils/synthetic';

let actions: typeof actionsType;
let actionsPromise: Promise<void>;

const useInitialize = () => {
  const store = useStore() as StoreWithReducerInjector;

  if (!actions) {
    // biome-ignore lint/suspicious/noAsyncPromiseExecutor: intentional
    actionsPromise ??= new Promise<void>(async resolve => {
      const {
        sliceName,
        reducer,
        actions: _actions,
        syntheticMiddleware,
      } = await import('redux/modules/synthetic');
      addAppMiddleware(syntheticMiddleware());
      store.injectReducer(sliceName, reducer);
      actions = _actions;
      await (store as any).dispatch(actions.initializeFromStorage());
      resolve();
    });
    throw actionsPromise;
  }
};

const COMMA_PLACEHOLDER = '__COMMA__';

interface Props {
  initOnly?: boolean;
}

const SyntheticMemoriesComponent: React.FC = () => {
  const dispatch = useDispatch<ThunkDispatch>();
  const hasTicEnabled = useSelector(getHasEnabledTic);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImportMemories = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const [file] = event.target.files ?? [];
    const text = await file.text();

    const specs = text
      .split(/\r?\n/)
      .slice(1)
      .map<MemorySpecBase>(line => {
        const [appName, title, details, url, from, to, project] = line
          .trim()
          .replaceAll(/,"([\s\S]*?)(,)([\s\S]*?)"/g, `,$1${COMMA_PLACEHOLDER}$3`)
          .split(',')
          .map(s => trim(s, ' "').replaceAll(COMMA_PLACEHOLDER, `,`));

        return {
          app: appName,
          fileName: title,
          details,
          url,
          from,
          to,
          project,
        };
      })
      .filter(spec => spec.app && spec.fileName);

    return dispatch(actions.generateMemoriesForSelectedDay(specs));
  };

  return (
    <>
      {hasTicEnabled ? (
        <Message size="small" variant="info" autoIcon style={{ marginBottom: '0.5rem' }}>
          TIC only supports dynamically generated synthetic memories at this time. If you rely on
          any of the buttons below, talk to Timm about it.
        </Message>
      ) : null}
      <Button
        disabled={hasTicEnabled}
        style={{ marginBottom: '0.5rem' }}
        onClick={() => dispatch(actions.generateMemoriesForSelectedDay())}
      >
        Generate memories
      </Button>
      <Button
        style={{ marginBottom: '0.5rem' }}
        disabled={hasTicEnabled}
        onClick={() => fileInputRef.current?.click()}
      >
        Import memories
      </Button>
      <Button
        disabled={hasTicEnabled}
        style={{ marginBottom: '0.5rem' }}
        onClick={() => dispatch(actions.generateAiDraftsForSelectedDay())}
      >
        Generate AI drafts
      </Button>
      <Button destructive onClick={() => dispatch(actions.clearSyntheticDataSettings())}>
        Clear all synthetic data settings for this workspace
      </Button>
      <input
        disabled={hasTicEnabled}
        ref={fileInputRef}
        name="Memories"
        type="file"
        accept=".csv,text/csv"
        onChange={handleImportMemories}
        hidden
      />
    </>
  );
};

export const SyntheticMemories: React.FC<Props> = ({ initOnly = false }) => {
  useInitialize();
  return !initOnly ? <SyntheticMemoriesComponent /> : null;
};
